import { z } from 'zod';
import { nonNegativePaiseSchema, paiseSchema } from '../money/paise.js';
import { paymentMethodSchema } from '../money/payment-methods.js';
import { indianMobileSchema, objectIdSchema } from './common.js';

/**
 * Work sent outside the shop (task 29).
 *
 * The shape deliberately mirrors task 21's payouts, because an owner should learn ONE mental
 * model for "somebody did work and is owed for it", not two. What a vendor is owed accrues on
 * the job, a settlement pays a batch of jobs with advances and deductions applied, and the
 * settlement posts an expense into the P&L. If you have read the payout schema, you have read
 * this one.
 *
 * What is genuinely different is the PHYSICAL half. A karigar's work never leaves the building;
 * an embroiderer's does, and pieces that leave the premises are the ones that get lost, delayed
 * and argued about. So a job-work order carries dispatch photographs, a promised return date, and
 * a quality check on the way back in — none of which a payout has any use for.
 */

// --- Vendors -----------------------------------------------------------------------------------

/** What outside units actually do. Free text would make the rate card unmatchable. */
export const WORK_TYPES = [
  'embroidery',
  'aari',
  'beadwork',
  'printing',
  'dyeing',
  'stitching',
  'finishing',
  'other',
] as const;
export type WorkType = (typeof WORK_TYPES)[number];
export const workTypeSchema = z.enum(WORK_TYPES);

/** How a vendor charges: per piece, or per garment-hour of an agreed job. */
export const VENDOR_RATE_BASES = ['per_piece', 'per_job'] as const;
export type VendorRateBasis = (typeof VENDOR_RATE_BASES)[number];
export const vendorRateBasisSchema = z.enum(VENDOR_RATE_BASES);

export const vendorRateSchema = z.object({
  workType: workTypeSchema,
  basis: vendorRateBasisSchema,
  ratePaise: nonNegativePaiseSchema,
  note: z.string().trim().max(200).nullable(),
});
export type VendorRate = z.infer<typeof vendorRateSchema>;

export const vendorSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string(),
  alternatePhone: z.string().nullable(),
  specialisations: z.array(workTypeSchema),
  address: z.string().nullable(),
  /** "15 days from delivery", "on settlement" — free text, because every unit words it its own way. */
  paymentTerms: z.string().nullable(),
  notes: z.string().nullable(),
  rates: z.array(vendorRateSchema),
  active: z.boolean(),
  /** What is owed right now, across every unsettled job. */
  outstandingPaise: paiseSchema,
  /** Advances paid and not yet applied to a settlement. */
  unappliedAdvancePaise: paiseSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Vendor = z.infer<typeof vendorSchema>;

export const createVendorSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: indianMobileSchema,
  alternatePhone: indianMobileSchema.optional(),
  specialisations: z.array(workTypeSchema).min(1).max(8),
  address: z.string().trim().max(300).optional(),
  paymentTerms: z.string().trim().max(200).optional(),
  notes: z.string().trim().max(500).optional(),
  rates: z
    .array(
      vendorRateSchema.omit({ note: true }).extend({ note: z.string().trim().max(200).optional() }),
    )
    .max(20)
    .default([]),
});
export type CreateVendorInput = z.infer<typeof createVendorSchema>;

export const updateVendorSchema = createVendorSchema.partial().extend({
  active: z.boolean().optional(),
});
export type UpdateVendorInput = z.infer<typeof updateVendorSchema>;

// --- Job-work orders ------------------------------------------------------------------------------

/** Where a job-work order stands. `partially_returned` is the common case, not the exception. */
export const JOB_WORK_STATUSES = ['sent', 'partially_returned', 'returned', 'cancelled'] as const;
export type JobWorkStatus = (typeof JOB_WORK_STATUSES)[number];
export const jobWorkStatusSchema = z.enum(JOB_WORK_STATUSES);

/** What happened to one piece on the way back in. */
export const PIECE_OUTCOMES = ['pending', 'accepted', 'rejected'] as const;
export type PieceOutcome = (typeof PIECE_OUTCOMES)[number];
export const pieceOutcomeSchema = z.enum(PIECE_OUTCOMES);

/** What a rejected piece does next. Recording which is the point of asking. */
export const REJECTION_ROUTES = ['return_to_vendor', 'rework_in_house'] as const;
export type RejectionRoute = (typeof REJECTION_ROUTES)[number];
export const rejectionRouteSchema = z.enum(REJECTION_ROUTES);

export const jobWorkPieceSchema = z.object({
  orderItemId: z.string(),
  orderId: z.string(),
  orderNumber: z.string(),
  lineNumber: z.number().int(),
  garmentTypeLabel: z.string(),
  customerName: z.string(),
  quantity: z.number().int(),
  /** Rate captured at dispatch — never looked up again, exactly as task 20 freezes a piece rate. */
  ratePaise: nonNegativePaiseSchema,
  amountPaise: paiseSchema,
  outcome: pieceOutcomeSchema,
  receivedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  rejectionRoute: rejectionRouteSchema.nullable(),
});
export type JobWorkPiece = z.infer<typeof jobWorkPieceSchema>;

export const jobWorkOrderSchema = z.object({
  id: z.string(),
  number: z.string(),
  vendorId: z.string(),
  vendorName: z.string(),
  workType: workTypeSchema,
  instructions: z.string().nullable(),
  sentAt: z.string(),
  promisedReturnDate: z.string().nullable(),
  status: jobWorkStatusSchema,
  pieces: z.array(jobWorkPieceSchema),
  /** Photographs of the pieces as they left. What settles a damage dispute. */
  dispatchMediaIds: z.array(z.string()),
  returnMediaIds: z.array(z.string()),
  totalPaise: paiseSchema,
  /** Accepted pieces only — a rejected piece is not work the shop owes for. */
  payablePaise: paiseSchema,
  deductionsPaise: paiseSchema,
  settledAt: z.string().nullable(),
  settlementId: z.string().nullable(),
  /** Days since dispatch for anything still out; null once everything is back. */
  daysOut: z.number().int().nullable(),
  overdue: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type JobWorkOrder = z.infer<typeof jobWorkOrderSchema>;

export const createJobWorkSchema = z.object({
  vendorId: objectIdSchema,
  workType: workTypeSchema,
  orderItemIds: z.array(objectIdSchema).min(1).max(100),
  /** Left out, the vendor's rate card for this work type is used. */
  ratePaise: nonNegativePaiseSchema.optional(),
  instructions: z.string().trim().max(500).optional(),
  promisedReturnDate: z.union([z.date(), z.string()]).optional(),
  dispatchMediaIds: z.array(objectIdSchema).max(20).default([]),
  sentAt: z.union([z.date(), z.string()]).optional(),
});
export type CreateJobWorkInput = z.infer<typeof createJobWorkSchema>;

/**
 * Pieces coming back.
 *
 * A list rather than the whole order, because a batch of twenty rarely comes back at once and a
 * return that could only be recorded in full would be recorded late, wrongly, or not at all.
 */
export const receiveJobWorkSchema = z.object({
  pieces: z
    .array(
      z.object({
        orderItemId: objectIdSchema,
        outcome: z.enum(['accepted', 'rejected']),
        rejectionReason: z.string().trim().max(300).optional(),
        rejectionRoute: rejectionRouteSchema.optional(),
      }),
    )
    .min(1)
    .max(100),
  receivedAt: z.union([z.date(), z.string()]).optional(),
  returnMediaIds: z.array(objectIdSchema).max(20).default([]),
});
export type ReceiveJobWorkInput = z.infer<typeof receiveJobWorkSchema>;

// --- Money ----------------------------------------------------------------------------------------

export const VENDOR_LEDGER_KINDS = ['advance', 'deduction', 'addition'] as const;
export type VendorLedgerKind = (typeof VENDOR_LEDGER_KINDS)[number];
export const vendorLedgerKindSchema = z.enum(VENDOR_LEDGER_KINDS);

export const vendorLedgerEntrySchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  kind: vendorLedgerKindSchema,
  amountPaise: paiseSchema,
  /** How much of an advance a settlement has already recovered. */
  appliedPaise: paiseSchema,
  reason: z.string().nullable(),
  method: paymentMethodSchema.nullable(),
  occurredAt: z.string(),
  settledAt: z.string().nullable(),
  recordedByName: z.string().nullable(),
});
export type VendorLedgerEntry = z.infer<typeof vendorLedgerEntrySchema>;

export const recordVendorAdvanceSchema = z.object({
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  method: paymentMethodSchema,
  occurredAt: z.union([z.date(), z.string()]).optional(),
  note: z.string().trim().max(300).optional(),
});
export type RecordVendorAdvanceInput = z.infer<typeof recordVendorAdvanceSchema>;

/** A deduction always carries a reason. "₹500 less" with no reason starts an argument. */
export const recordVendorDeductionSchema = z.object({
  amountPaise: paiseSchema.min(1, 'Enter an amount'),
  reason: z.string().trim().min(1, 'Say why').max(300),
  occurredAt: z.union([z.date(), z.string()]).optional(),
});
export type RecordVendorDeductionInput = z.infer<typeof recordVendorDeductionSchema>;

export const settleVendorSchema = z.object({
  jobWorkIds: z.array(objectIdSchema).min(1).max(200),
  method: paymentMethodSchema,
  paidAt: z.union([z.date(), z.string()]).optional(),
  note: z.string().trim().max(300).optional(),
});
export type SettleVendorInput = z.infer<typeof settleVendorSchema>;

export const vendorSettlementSchema = z.object({
  id: z.string(),
  number: z.string(),
  vendorId: z.string(),
  vendorName: z.string(),
  paidAt: z.string(),
  method: paymentMethodSchema,
  /** What the accepted pieces came to. */
  jobsPaise: paiseSchema,
  deductionsPaise: paiseSchema,
  advanceAppliedPaise: paiseSchema,
  /** jobs − deductions − advance applied. Never negative. */
  netPaise: paiseSchema,
  jobWorkIds: z.array(z.string()),
  expenseId: z.string().nullable(),
  note: z.string().nullable(),
  recordedByName: z.string().nullable(),
});
export type VendorSettlement = z.infer<typeof vendorSettlementSchema>;

// --- Views -----------------------------------------------------------------------------------------

export const vendorStatementSchema = z.object({
  vendor: vendorSchema,
  /** Jobs that are back, accepted and unsettled — what a settlement would pay for. */
  unsettled: z.array(jobWorkOrderSchema),
  unsettledPaise: paiseSchema,
  pendingDeductionsPaise: paiseSchema,
  unappliedAdvancePaise: paiseSchema,
  /** unsettled − deductions − advances, floored at zero. */
  netPayablePaise: paiseSchema,
  ledger: z.array(vendorLedgerEntrySchema),
  settlements: z.array(vendorSettlementSchema),
});
export type VendorStatement = z.infer<typeof vendorStatementSchema>;

export const outsidePremisesSchema = z.object({
  jobs: z.array(jobWorkOrderSchema),
  pieceCount: z.number().int(),
  overdueCount: z.number().int(),
  totalValuePaise: paiseSchema,
});
export type OutsidePremises = z.infer<typeof outsidePremisesSchema>;

export const vendorPerformanceRowSchema = z.object({
  vendorId: z.string(),
  vendorName: z.string(),
  jobsSent: z.number().int(),
  piecesSent: z.number().int(),
  piecesReturned: z.number().int(),
  piecesRejected: z.number().int(),
  /** rejected / returned. Display only. */
  rejectionRate: z.number(),
  /** Dispatch to full return, averaged over completed jobs. Null when none have come back. */
  averageTurnaroundMs: z.number().int().nullable(),
  lateReturns: z.number().int(),
  billedPaise: paiseSchema,
});
export type VendorPerformanceRow = z.infer<typeof vendorPerformanceRowSchema>;

export const vendorPerformanceSchema = z.object({
  from: z.string(),
  to: z.string(),
  rows: z.array(vendorPerformanceRowSchema),
  totalOutstandingPaise: paiseSchema,
});
export type VendorPerformance = z.infer<typeof vendorPerformanceSchema>;
